import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Button, Field, Input, RadioGroup, Select, StatusPill } from '@bb/ui';
import type { StatusTone } from '@bb/ui';
import { useState } from 'react';
import { ApiError, type AdminApi, type CreatedPreviewLink, type CreatePreviewLinkBody, type PreviewLinkChannel, type PreviewLinkView } from '../../../api/client.ts';
import { CHANNEL_LABELS } from './personalization.ts';
import { useTestUsers } from './ProfileSelector.tsx';

/** What a test renders: the content being edited, or the campaign's saved variant. */
export type PreviewLinkSource = { draftContent: Record<string, unknown> } | { messageRef: { campaignId: string; variantApiId?: string } };

export interface PreviewLinksSectionProps {
  environmentId: string;
  channel: PreviewLinkChannel;
  /** `null` while there is nothing to render or a JSON field is invalid. */
  source: PreviewLinkSource | null;
  overrides?: Record<string, unknown>;
  /** `eventPropertiesSample`, `apiTriggerPropertiesSample`, `contextSample`. */
  samples: Record<string, Record<string, unknown>>;
  /** The campaign whose links are listed; without it only the link just created is shown. */
  campaignId?: string;
}

const STATE_TONES: Record<PreviewLinkView['state'], StatusTone> = { active: 'success', expired: 'neutral', revoked: 'danger' };
const STATE_LABELS: Record<PreviewLinkView['state'], string> = { active: 'Active', expired: 'Expired', revoked: 'Revoked' };

/** `2026-10-24 12:00 UTC`: the same for every viewer. */
export const utcTime = (iso: string) => `${iso.slice(0, 16).replace('T', ' ')} UTC`;

function createErrorText(error: unknown): string {
  if (!(error instanceof ApiError)) return 'The link could not be created.';
  if (error.code === 'test_recipient_not_allowed') return 'A preview link renders only a user of a content test group, or no user.';
  if (error.code === 'no_content') return 'The campaign has no saved content for this channel yet: wait for the draft to save and try again.';
  if (error.status === 429) return 'Too many preview links: wait a minute and try again.';
  if (error.status === 403) return 'Preview links need the messages.test_send permission.';
  return error.message;
}

/**
 * Preview links (BB-59): a link colleagues open without signing in, valid for 7 days, showing the message rendered
 * once at creation for no user (attribute overrides only) or a content-test user, never with personal data. The
 * link is shown only when it is created; the campaign's links can be revoked.
 */
export function PreviewLinksSection({ environmentId, channel, source, overrides, samples, campaignId }: PreviewLinksSectionProps) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<'synthetic' | 'test_profile'>('synthetic');
  const [profileId, setProfileId] = useState('');
  const [created, setCreated] = useState<CreatedPreviewLink>();
  const [copy, setCopy] = useState<'copied' | 'failed'>();
  const testUsers = useTestUsers(environmentId, kind === 'test_profile');
  const listKey = ['preview-links', environmentId, campaignId];
  const links = useQuery({ queryKey: listKey, enabled: Boolean(campaignId), queryFn: () => api.listPreviewLinks(environmentId, campaignId) });

  const body: CreatePreviewLinkBody | null =
    source && (kind === 'synthetic' || profileId)
      ? { channel, ...source, profile: kind === 'synthetic' ? { kind: 'synthetic' } : { kind: 'test_profile', profileId }, ...(overrides ? { attributeOverrides: overrides } : {}), ...samples }
      : null;
  const create = useMutation({
    mutationFn: (request: CreatePreviewLinkBody) => api.createPreviewLink(environmentId, request),
    onSuccess: (link) => {
      setCreated(link);
      setCopy(undefined);
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });
  const revoke = useMutation({
    mutationFn: (linkId: string) => api.revokePreviewLink(environmentId, linkId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: listKey }),
  });
  const copyLink = (url: string) =>
    navigator.clipboard
      .writeText(url)
      .then(() => setCopy('copied'))
      .catch(() => setCopy('failed'));

  return (
    <div className="stack-form" aria-label="Preview links" role="group">
      <RadioGroup
        label="Render for"
        options={[
          { value: 'synthetic', label: 'No user: attribute overrides only' },
          { value: 'test_profile', label: 'A test user' },
        ]}
        value={kind}
        onValueChange={(next) => setKind(next as 'synthetic' | 'test_profile')}
      />
      {kind === 'test_profile' && testUsers.data && (
        <Field label="Test user" hint="Only users of content test groups; personal data is masked.">
          <Select placeholder="Select…" options={testUsers.data.map((m) => ({ value: m.profileId, label: `${m.externalId ?? m.emailMasked ?? m.profileId} (${m.group})` }))} value={profileId} onChange={(e) => setProfileId(e.target.value)} />
        </Field>
      )}
      {campaignId && <p className="hint">The link shows the campaign’s saved draft as it is now: the wizard saves it a moment after each change.</p>}
      <div className="toolbar">
        <Button variant="primary" onClick={() => body && create.mutate(body)} loading={create.isPending} disabled={!body}>
          Create preview link
        </Button>
      </div>
      {create.isError && <p role="alert">{createErrorText(create.error)}</p>}
      {created && (
        <div className="notice notice--neutral" role="status">
          <p>
            {CHANNEL_LABELS[created.channel]} preview link, valid until {utcTime(created.expiresAt)}. Copy it now: it is shown only once.
          </p>
          <div className="inline-form">
            <Input aria-label="Preview link" readOnly value={created.url} onFocus={(e) => e.target.select()} />
            <Button size="sm" onClick={() => void copyLink(created.url)}>
              Copy link
            </Button>
          </div>
          {copy === 'copied' && <p className="hint">Link copied.</p>}
          {copy === 'failed' && <p className="hint">The browser did not allow copying: select the link and copy it.</p>}
        </div>
      )}
      {campaignId && (
        <section aria-label="Campaign preview links">
          <h4>Links of this campaign</h4>
          {links.isPending && <p aria-busy="true">Loading links…</p>}
          {links.isError && <p role="alert">Preview links could not be loaded.</p>}
          {links.data?.length === 0 && <p className="hint">No preview links yet.</p>}
          {revoke.isError && <p role="alert">The link could not be revoked.</p>}
          {links.data && links.data.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Channel</th>
                  <th scope="col">Rendered for</th>
                  <th scope="col">Created</th>
                  <th scope="col">Expires</th>
                  <th scope="col">State</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {links.data.map((link) => (
                  <tr key={link.id}>
                    <td>{CHANNEL_LABELS[link.channel]}</td>
                    <td>{link.profileKind === 'synthetic' ? 'No user' : 'Test user'}</td>
                    <td>{utcTime(link.createdAt)}</td>
                    <td>{utcTime(link.expiresAt)}</td>
                    <td>
                      <StatusPill tone={STATE_TONES[link.state]}>{STATE_LABELS[link.state]}</StatusPill>
                    </td>
                    <td>
                      {link.state === 'active' && (
                        <Button size="sm" variant="danger" aria-label={`Revoke link created ${utcTime(link.createdAt)}`} loading={revoke.isPending && revoke.variables === link.id} onClick={() => revoke.mutate(link.id)}>
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}
